import { FC } from "react";
import { Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
	ChainTransportModelType,
	CountriesEmissionsModelType,
	TransportModelType,
	TypeTransportModelType,
} from "../../../../../common/modelTypes";
import { colGrow, colGrowXs6 } from "../../../../../common/utils";
import PieChartDistributionType from "../../../../../components/charts/graph/PieChartDistributionType";
import NumericValueWidget from "../../../../../components/charts/NumericValueWidget";
import { AmbientalKpiWidget } from "../../../../../components/common/AmbientalKpiWidget";
import ContentLocked from "../../../../../components/common/ContentLocked";
import { MarketBaseType, MarketBaseWidget } from "../../../../../components/common/MarketBaseWidget";
import PermissionGridItem from "../../../../../components/common/PermissionGridItem";
import TableData from "../../../../../components/common/table/TableData";
import { EmissionsTransportsResultsDatumDTO } from "../../../../../datasources/api-client/data-contracts";
import { getLockedContents } from "../../../../../permissions/permissionUtils";
import { ThemeColors, TransportTypesColors } from "../../../../../theme/themeValues";
import EmissionChaiTransport from "../../components/EmissionChainTransport";
import EmissionsMap from "../../components/EmissionsMap";
import { dataCol, getColumns, getDataChart, getRows } from "./data";

type EmissionType = keyof Pick<EmissionsTransportsResultsDatumDTO, "co2Ttw" | "co2Wtw">;

interface Props {
	keyEmissions: string;
	transportEmissions: TransportModelType;
	emissionTypeTransport: TypeTransportModelType;
	emissionsChainTransport: ChainTransportModelType;
	emissionsMap: CountriesEmissionsModelType;
	selectedSource: string;
	onSourceChange: (value: string) => void;
}

const Greenrouter: FC<Props> = ({
	transportEmissions,
	keyEmissions,
	emissionTypeTransport,
	emissionsChainTransport,
	emissionsMap,
	selectedSource,
	onSourceChange,
}) => {
	const { t } = useTranslation(["result.transport", "common"]);

	const colors = [
		TransportTypesColors.LightRoad,
		TransportTypesColors.LightTrain,
		TransportTypesColors.LightShip,
		TransportTypesColors.LightAir,
	];

	const overrideFieldName = (old?: string) => {
		switch (old) {
			case "emissions":
				return keyEmissions;
			default:
				return old;
		}
	};

	const isWtw = keyEmissions === "co2Wtw";

	const marketBase: MarketBaseType = {
		emissions: isWtw
			? transportEmissions.data?.greenRouter?.current.marketBasedWtw
			: transportEmissions.data?.greenRouter?.current.marketBasedTtw,
		delta: isWtw
			? transportEmissions.data?.greenRouter?.current.marketBasedWtwDelta
			: transportEmissions.data?.greenRouter?.current.marketBasedTtwDelta,
		coverage: null,
		isLoading: transportEmissions.isLoading,
		onReload: transportEmissions.reload,
	};

	const titles = {
		transportEmissions: t("transport emissions"),
		numberOfTransports: t("number of transports"),
		averageWeight: t("average weight for transport"),
		averageDistance: t("average distance for transport"),
		emissionsByTransportMode: t("emissions by transport mode"),
		distributionType: t("distribution type"),
		emissionsMap: t("emissions for"),
		transportChain: t("transport chain results"),
		environmentalKpis: t("common:environmental kpis"),
		marketBased: t("common:market-based measures"),
	};

	const lockedContents = getLockedContents([
		{
			models: [transportEmissions],
			names: [
				titles.transportEmissions,
				titles.numberOfTransports,
				titles.averageWeight,
				titles.averageDistance,
				titles.environmentalKpis,
				titles.marketBased,
			],
		},
		{ models: [emissionTypeTransport], names: [titles.emissionsByTransportMode, titles.distributionType] },
		{ models: [emissionsMap], names: [titles.emissionsMap] },
		{ models: [emissionsChainTransport], names: [titles.transportChain] },
	]);

	return (
		<Grid container spacing={2}>
			<Grid container size={12} spacing={2}>
				<PermissionGridItem models={[transportEmissions]} size={colGrowXs6} minHeight={180}>
					<NumericValueWidget
						title={titles.transportEmissions}
						delta={transportEmissions?.data?.greenRouter.delta?.[keyEmissions as EmissionType]}
						value={transportEmissions.data?.greenRouter.current?.[keyEmissions as EmissionType]?.value}
						measure={transportEmissions.data?.greenRouter.current?.[keyEmissions as EmissionType]?.measure}
						isLoading={transportEmissions.isLoading}
						onReload={transportEmissions.reload}
					/>
				</PermissionGridItem>
				<PermissionGridItem models={[transportEmissions]} size={colGrowXs6} minHeight={180}>
					<NumericValueWidget
						title={titles.numberOfTransports}
						value={transportEmissions.data?.greenRouter?.current?.count}
						isLoading={transportEmissions.isLoading}
						onReload={transportEmissions.reload}
					/>
				</PermissionGridItem>
				<PermissionGridItem models={[transportEmissions]} size={colGrowXs6} minHeight={180}>
					<NumericValueWidget
						title={titles.averageWeight}
						value={transportEmissions.data?.greenRouter?.current?.avgWeight.value}
						measure={transportEmissions.data?.greenRouter?.current?.weight.measure + "/" + t("transport")}
						isLoading={transportEmissions.isLoading}
						onReload={transportEmissions.reload}
					/>
				</PermissionGridItem>
				<PermissionGridItem models={[transportEmissions]} size={colGrowXs6} minHeight={180}>
					<NumericValueWidget
						title={titles.averageDistance}
						value={transportEmissions.data?.greenRouter?.current?.avgDistance.value}
						measure={transportEmissions.data?.greenRouter?.current?.distance.measure}
						isLoading={transportEmissions.isLoading}
						onReload={transportEmissions.reload}
					/>
				</PermissionGridItem>
			</Grid>

			<Grid container size={12} spacing={2}>
				<PermissionGridItem models={[emissionTypeTransport]} size={colGrow} minHeight={360}>
					<TableData
						columns={getColumns(
							dataCol,
							emissionTypeTransport.data ? emissionTypeTransport.data.total : {},
							t,
							overrideFieldName
						)}
						rows={emissionTypeTransport.data ? getRows(emissionTypeTransport.data, keyEmissions, t) : []}
						title={titles.emissionsByTransportMode}
						isLoading={emissionTypeTransport.isLoading}
						onReload={emissionTypeTransport.reload}
					/>
				</PermissionGridItem>
				<PermissionGridItem models={[emissionTypeTransport]} size={colGrow} minHeight={360}>
					<PieChartDistributionType
						totalEmissions={emissionTypeTransport.data?.total?.[keyEmissions as EmissionType]?.value || 0}
						keyEmissions={keyEmissions}
						title={titles.distributionType}
						isLoading={emissionTypeTransport.isLoading}
						onReload={emissionTypeTransport.reload}
						dataChart={
							emissionTypeTransport.data && getDataChart(emissionTypeTransport.data, keyEmissions, t)
						}
						colors={colors}
					/>
				</PermissionGridItem>
			</Grid>

			<Grid container size={12} spacing={2}>
				<PermissionGridItem models={[emissionsMap]} size={colGrow} minHeight={400}>
					<EmissionsMap
						title={titles.emissionsMap}
						color={ThemeColors.DarkPrimary}
						data={emissionsMap.data}
						isLoading={emissionsMap.isLoading}
						onReload={emissionsMap.reload}
						selectedSource={selectedSource}
						onSourceChange={onSourceChange}
					/>
				</PermissionGridItem>
				<PermissionGridItem models={[emissionsChainTransport]} size={colGrow}>
					<EmissionChaiTransport
						title={titles.transportChain}
						data={emissionsChainTransport.data}
						isLoading={emissionsChainTransport.isLoading}
						onReload={emissionsChainTransport.reload}
					/>
				</PermissionGridItem>
			</Grid>

			<PermissionGridItem models={[transportEmissions]} size={12}>
				<AmbientalKpiWidget
					bio={transportEmissions.data?.greenRouter.current.biogenic}
					pmx={transportEmissions.data?.greenRouter.current.pmx}
					loading={transportEmissions.isLoading}
					onReload={transportEmissions.reload}
				/>
			</PermissionGridItem>

			<PermissionGridItem models={[transportEmissions]} size={12}>
				<MarketBaseWidget marketBase={marketBase} />
			</PermissionGridItem>

			{lockedContents.length > 0 && (
				<Grid size={12}>
					<ContentLocked items={lockedContents} />
				</Grid>
			)}
		</Grid>
	);
};

export default Greenrouter;
